import React, { useState, useEffect } from 'react';
import { ledgerApi, sitesApi, entitiesApi, exportApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Filter, Layers, ChevronDown, ChevronRight, FileSpreadsheet, Download, Building2, Paperclip } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { CATEGORIES, CATEGORY_LABELS } from '@/constants/categories';

export default function CombinedLedgerPage() {
  const [sites, setSites] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [filters, setFilters] = useState({ site_id: '', category: '', date_from: '', date_to: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [entitySearch, setEntitySearch] = useState('');
  const [includeBills, setIncludeBills] = useState(false);

  useEffect(() => {
    sitesApi.list().then(r => setSites(r.data)).catch(() => {});
    entitiesApi.list().then(r => setEntities(r.data)).catch(() => {});
  }, []);

  const toggleEntity = (id) => {
    setSelectedEntityIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllEntities = () => {
    const filteredIds = filteredEntities.map(e => e._id);
    setSelectedEntityIds(filteredIds);
  };

  const clearAllEntities = () => setSelectedEntityIds([]);

  const fetchCombinedLedger = async () => {
    if (selectedEntityIds.length === 0) return;
    setLoading(true);
    try {
      const params = { entity_ids: selectedEntityIds.join(',') };
      if (filters.site_id) params.site_id = filters.site_id;
      if (filters.category) params.category = filters.category;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await ledgerApi.combined(params);
      setResult(data);
      setCollapsedSections({});
    } catch {}
    setLoading(false);
  };

  const toggleSection = (eid) => {
    setCollapsedSections(prev => ({ ...prev, [eid]: !prev[eid] }));
  };

  const buildExportParams = () => {
    const params = { entity_ids: selectedEntityIds.join(',') };
    if (filters.site_id) params.site_id = filters.site_id;
    if (filters.category) params.category = filters.category;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  };

  const handleCombinedExport = (type, perspective) => {
    const params = { ...buildExportParams() };
    if (perspective) params.perspective = perspective;
    if (includeBills && type === 'pdf') params.include_bills = 'true';
    const urlMap = { excel: exportApi.combinedExcel, pdf: exportApi.combinedPdf };
    window.open(urlMap[type](params), '_blank');
  };

  const filteredEntities = entities.filter(e => {
    if (entitySearch && !e.name.toLowerCase().includes(entitySearch.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="combined-ledger-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Combined Ledger</h1>
        <p className="text-sm text-muted-foreground mt-1">Generate ledger reports for multiple agencies &amp; vendors combined</p>
      </div>

      {/* Entity Selector */}
      <div className="bg-card border border-border p-4 space-y-3" data-testid="entity-selector">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
            <Layers className="w-3.5 h-3.5" /> Select Entities ({selectedEntityIds.length} selected)
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={selectAllEntities} data-testid="select-all-entities">Select All</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={clearAllEntities} data-testid="clear-all-entities">Clear</Button>
          </div>
        </div>
        <Input
          value={entitySearch}
          onChange={e => setEntitySearch(e.target.value)}
          placeholder="Search entities..."
          className="h-8 text-xs"
          data-testid="entity-search"
        />
        <div className="max-h-48 overflow-y-auto border border-border rounded p-2 space-y-1">
          {filteredEntities.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-xs">No entities found</div>
          ) : filteredEntities.map(entity => (
            <label key={entity._id} className="flex items-center gap-2.5 py-1.5 px-2 hover:bg-muted/50 rounded cursor-pointer text-sm" data-testid={`entity-checkbox-${entity._id}`}>
              <Checkbox
                checked={selectedEntityIds.includes(entity._id)}
                onCheckedChange={() => toggleEntity(entity._id)}
              />
              <span className="flex-1 truncate">{entity.name}</span>
              <span className="text-xs text-muted-foreground capitalize">{entity.entity_type}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border p-4 space-y-3" data-testid="combined-filters">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Site</Label>
            <Select value={filters.site_id || '__all__'} onValueChange={v => setFilters(f => ({ ...f, site_id: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="combined-filter-site"><SelectValue placeholder="All Sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Sites</SelectItem>
                {sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Category</Label>
            <Select value={filters.category || '__all__'} onValueChange={v => setFilters(f => ({ ...f, category: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="combined-filter-category"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">From Date</Label>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="combined-filter-from" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To Date</Label>
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="combined-filter-to" />
          </div>
        </div>
        <Button size="sm" className="h-8" onClick={fetchCombinedLedger} disabled={selectedEntityIds.length === 0 || loading} data-testid="generate-combined-ledger">
          {loading ? 'Generating...' : `Generate Combined Ledger (${selectedEntityIds.length} entities)`}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6" data-testid="combined-results">
          {/* Export Buttons */}
          <div className="flex gap-2 items-center flex-wrap">
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="combined-include-bills-toggle">
              <Switch checked={includeBills} onCheckedChange={setIncludeBills} className="scale-75" />
              <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground whitespace-nowrap">With Bills</span>
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="combined-export-excel">
                  <FileSpreadsheet className="w-4 h-4" /> Export Excel <ChevronDown className="w-3 h-3 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleCombinedExport('excel')} data-testid="combined-excel-ours">Our Perspective</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleCombinedExport('excel', 'counterparty')} data-testid="combined-excel-counter"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Counterparty View</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="combined-export-pdf">
                  <Download className="w-4 h-4" /> Export PDF <ChevronDown className="w-3 h-3 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleCombinedExport('pdf')} data-testid="combined-pdf-ours">Our Perspective</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleCombinedExport('pdf', 'counterparty')} data-testid="combined-pdf-counter"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Counterparty View</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Grand Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3 sm:p-4" data-testid="combined-total-in">
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">Grand Total In</div>
              <div className="font-mono text-lg sm:text-xl font-bold text-emerald-700 dark:text-emerald-400 mt-1">{'\u20B9'}{result.grand_total_in.toLocaleString('en-IN')}</div>
            </div>
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-3 sm:p-4" data-testid="combined-total-out">
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-red-700 dark:text-red-400 font-semibold">Grand Total Out</div>
              <div className="font-mono text-lg sm:text-xl font-bold text-red-700 dark:text-red-400 mt-1">{'\u20B9'}{result.grand_total_out.toLocaleString('en-IN')}</div>
            </div>
            <div className="bg-card border border-border p-3 sm:p-4" data-testid="combined-balance">
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-semibold">Grand Balance</div>
              <div className={`font-mono text-lg sm:text-xl font-bold mt-1 ${result.grand_balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{'\u20B9'}{result.grand_balance.toLocaleString('en-IN')}</div>
            </div>
          </div>

          {/* Per-Entity Sections */}
          {result.sections.map(section => (
            <div key={section.entity_id} className="bg-card border border-border" data-testid={`section-${section.entity_id}`}>
              {/* Section Header */}
              <button
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                onClick={() => toggleSection(section.entity_id)}
                data-testid={`toggle-section-${section.entity_id}`}
              >
                <div className="flex items-center gap-3">
                  {collapsedSections[section.entity_id] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <span className="font-bold text-sm">{section.entity_name}</span>
                  <span className="text-xs text-muted-foreground">({section.entries.length} entries)</span>
                </div>
                <div className="flex gap-6 text-xs font-mono">
                  <span className="text-emerald-700 dark:text-emerald-400">In: {'\u20B9'}{section.total_in.toLocaleString('en-IN')}</span>
                  <span className="text-red-700 dark:text-red-400">Out: {'\u20B9'}{section.total_out.toLocaleString('en-IN')}</span>
                  <span className={section.balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}>
                    Bal: {'\u20B9'}{section.balance.toLocaleString('en-IN')}
                  </span>
                </div>
              </button>

              {/* Section Table */}
              {!collapsedSections[section.entity_id] && section.entries.length > 0 && (
                <div className="border-t border-border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Date</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Type</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Category</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Mode</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Notes</th>
                        <th className="text-right py-2 px-3 font-semibold text-muted-foreground">In</th>
                        <th className="text-right py-2 px-3 font-semibold text-muted-foreground">Out</th>
                        <th className="text-right py-2 px-3 font-semibold text-muted-foreground">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.entries.map(entry => (
                        <tr key={entry._id} className="border-b border-border hover:bg-muted/30">
                          <td className="py-2 px-3">{entry.date}</td>
                          <td className="py-2 px-3">
                            <span className={entry.entry_type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}>
                              {entry.entry_type === 'income' ? 'In' : 'Out'}
                            </span>
                          </td>
                          <td className="py-2 px-3 capitalize">{CATEGORY_LABELS[entry.category] || entry.category}</td>
                          <td className="py-2 px-3 capitalize">{entry.payment_mode}</td>
                          <td className="py-2 px-3 max-w-[150px] truncate" title={entry.notes}>{entry.notes || '-'}</td>
                          <td className="py-2 px-3 text-right font-mono text-emerald-700 dark:text-emerald-400">
                            {entry.entry_type === 'income' ? `\u20B9${entry.amount.toLocaleString('en-IN')}` : ''}
                          </td>
                          <td className="py-2 px-3 text-right font-mono text-red-700 dark:text-red-400">
                            {entry.entry_type === 'expense' ? `\u20B9${entry.amount.toLocaleString('en-IN')}` : ''}
                          </td>
                          <td className={`py-2 px-3 text-right font-mono ${entry.running_balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                            {'\u20B9'}{entry.running_balance.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!collapsedSections[section.entity_id] && section.entries.length === 0 && (
                <div className="border-t border-border py-6 text-center text-muted-foreground text-xs">No entries for this entity</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
